/* ========================
 * CustomBotModal - Modal for creating, editing, and deleting custom bots
 * Handles form state, validation, and interaction with BotManager
 * ========================*/
import React, { useState, useEffect } from 'react';
import { botManager } from '@/managers/BotManager';
import { X, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { HousePortal } from '@/App';

const CustomBotModal = ({ isOpen, onClose, onBotCreated, botId, onBotUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    customSystemMessage: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [botManagerState, setBotManagerState] = useState(botManager.getState());

  // Subscribe to BotManager state changes
  useEffect(() => {
    const unsubscribe = botManager.subscribe((newState) => {
      setBotManagerState(newState);
    });
    return unsubscribe;
  }, []);

  // Initialize form data when modal opens or botId changes
  useEffect(() => {
    if (!isOpen) return;

    const { allBots } = botManagerState;

    if (botId) {
      // Editing existing custom bot
      const customBot = allBots.find(bot => bot.id === botId && bot.isCustomBot);
      if (customBot) {
        setFormData({
          name: customBot.name || '',
          customSystemMessage: customBot.customSystemMessage || '',
          description: customBot.description || ''
        });
      }
    } else {
      // Creating new custom bot - reset form
      setFormData({
        name: '',
        customSystemMessage: '',
        description: ''
      });
    }
  }, [botId, isOpen, botManagerState]);

  const handleSubmit = async () => {
    try {
      setError('');
      setIsSubmitting(true);

      if (!formData.name?.trim()) {
        setError('Please enter a name');
        return;
      }
      if (!formData.customSystemMessage?.trim()) {
        setError('Please enter a system message');
        return;
      }

      const botData = {
        name: formData.name.trim(),
        customSystemMessage: formData.customSystemMessage.trim(),
        description: formData.description?.trim() || '',
        // Custom bots don't need a baseModelId - they work with all models like vanilla mode
        ...(botId && { id: botId })
      };

      const savedBot = await botManager.saveCustomBot(botData);
      
      if (botId) {
        // The onBotUpdated callback is no longer necessary. BotManager's real-time
        // listener will detect the change and notify all subscribed components.
        // This avoids the error and adheres to a more robust, reactive architecture.
      } else {
        if (savedBot && onBotCreated) {
          onBotCreated(savedBot.id);
        }
      }

      onClose();

    } catch (err) {
      setError(err.message || 'Failed to save/update custom bot');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    setShowConfirmDelete(true);
  };

  const confirmedDelete = async () => {
    if (!botId) return;
    try {
      setIsSubmitting(true);
      await botManager.deleteCustomBot(botId);
      // The onBotUpdated callback is removed. BotManager's onSnapshot listener
      // handles the deletion and updates the UI automatically.
      onClose();
    } catch (error) {
      setError('Failed to delete bot');
    } finally {
      setIsSubmitting(false);
      setShowConfirmDelete(false);
    }
  };

  const cancelDelete = () => {
    setShowConfirmDelete(false);
  };

  return (
    <HousePortal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[5500]"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-center min-h-screen px-4">
              <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                onClick={!isSubmitting ? onClose : undefined}
              />

              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="relative w-full max-w-lg p-4 bg-surface rounded-lg shadow-glass"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-medium text-on-surface">
                    {botId ? 'Edit Custom Bot' : 'Create Custom Bot'}
                  </h2>

                  {/* Button Group */}
                  <div className="flex items-center gap-2">
                    {botId && (
                      <button
                        onClick={handleDelete}
                        disabled={isSubmitting}
                        className="p-1.5 rounded-md text-error hover:bg-error/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {!isSubmitting && (
                      <button
                        onClick={onClose}
                        className="p-1.5 rounded-md hover:bg-surface-dark transition-colors"
                      >
                        <X className="w-4 h-4 text-on-surface-dim" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Bot Form */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-on-surface-dim mb-1">
                      Bot Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        name: e.target.value
                      }))}
                      disabled={isSubmitting}
                      className="w-full px-3 py-1.5 bg-surface-dark border border-border-color 
                               rounded-md text-sm text-on-surface focus:border-primary 
                               focus:ring-1 focus:ring-primary transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Enter bot name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-on-surface-dim mb-1">
                      Description (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        description: e.target.value
                      }))}
                      disabled={isSubmitting}
                      className="w-full px-3 py-1.5 bg-surface-dark border border-border-color 
                               rounded-md text-sm text-on-surface focus:border-primary 
                               focus:ring-1 focus:ring-primary transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Brief description of your bot"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-on-surface-dim mb-1">
                      System Message
                    </label>
                    <textarea
                      value={formData.customSystemMessage}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        customSystemMessage: e.target.value
                      }))}
                      disabled={isSubmitting}
                      className="w-full px-3 py-1.5 bg-surface-dark border border-border-color 
                               rounded-md text-sm text-on-surface focus:border-primary 
                               focus:ring-1 focus:ring-primary transition-colors min-h-[100px]
                               disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Enter system message that defines your bot's behavior"
                    />
                  </div>

                  {error && (
                    <div className="text-error text-xs">
                      {error}
                    </div>
                  )}

                  <div className="flex gap-2 pt-3">
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="flex-1 px-3 py-1.5 bg-primary hover:bg-primary-light 
                               text-white text-sm rounded-md transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (botId ? 'Saving...' : 'Creating...') : (botId ? 'Save Changes' : 'Create Bot')}
                    </button>
                    {!isSubmitting && (
                      <button
                        onClick={onClose}
                        className="px-3 py-1.5 border border-border-color text-sm 
                                 text-on-surface-dim hover:bg-surface-dark rounded-md 
                                 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Confirmation Dialog */}
              <AnimatePresence>
                {showConfirmDelete && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 z-[5200] flex items-center justify-center"
                  >
                    <div
                      className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                      onClick={cancelDelete}
                    />
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.95, opacity: 0 }}
                      className="relative bg-surface-dark p-4 rounded-lg shadow-lg w-full max-w-md"
                    >
                      <p className="text-sm text-on-surface-dim mb-4">
                        Are you sure you want to delete this bot? This action cannot be undone.
                      </p>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={cancelDelete}
                          className="px-3 py-1.5 border border-border-color text-sm text-on-surface-dim hover:bg-surface-dark rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={confirmedDelete}
                          className="px-3 py-1.5 bg-error hover:bg-error/80 text-white text-sm rounded-md transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </HousePortal>
  );
};

export default CustomBotModal;